"use client";

import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { parseCurrency } from "@/lib/format";
import { Plus, Fuel, Car, ParkingCircle, MoreHorizontal } from "lucide-react";

interface ExpenseFormSheetProps {
  blockId?: Id<"blocks">;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  // Edit mode props
  mode?: "create" | "edit";
  expense?: {
    _id: Id<"expenses">;
    type: "fuel" | "tolls" | "parking" | "other";
    amount: number;
    gallons?: number;
    pricePerGallon?: number;
    rewardsPercent?: number;
    upsidePerGallon?: number;
    description?: string;
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const EXPENSE_TYPES = [
  { value: "fuel", label: "Fuel", icon: Fuel },
  { value: "tolls", label: "Tolls", icon: Car },
  { value: "parking", label: "Parking", icon: ParkingCircle },
  { value: "other", label: "Other", icon: MoreHorizontal },
] as const;

export function ExpenseFormSheet({
  blockId,
  trigger,
  onSuccess,
  mode = "create",
  expense,
  open,
  onOpenChange,
}: ExpenseFormSheetProps) {
  // Use controlled state when props provided
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [type, setType] = useState<"fuel" | "tolls" | "parking" | "other">("fuel");
  const [amount, setAmount] = useState("");
  const [gallons, setGallons] = useState("");
  const [pricePerGallon, setPricePerGallon] = useState("");
  const [rewardsPercent, setRewardsPercent] = useState("");
  const [upsidePerGallon, setUpsidePerGallon] = useState("");
  const [description, setDescription] = useState("");

  const createExpense = useMutation(api.expenses.createExpense);
  const updateExpense = useMutation(api.expenses.updateExpense);

  // Initialize form with expense data when editing
  useEffect(() => {
    if (mode === "edit" && expense && isOpen) {
      setType(expense.type);
      setAmount(expense.amount ? (expense.amount / 100).toFixed(2) : "");
      setGallons(expense.gallons?.toString() ?? "");
      setPricePerGallon(expense.pricePerGallon ? (expense.pricePerGallon / 100).toFixed(3) : "");
      setRewardsPercent(expense.rewardsPercent?.toString() ?? "");
      setUpsidePerGallon(expense.upsidePerGallon ? (expense.upsidePerGallon / 100).toFixed(2) : "");
      setDescription(expense.description ?? "");
    }
  }, [mode, expense, isOpen]);

  // Parse price per gallon (user enters full price with 3 decimals)
  const parsedPrice = type === "fuel" && pricePerGallon
    ? parseFloat(pricePerGallon)
    : 0;

  // Auto-calculate amount when gallons + price are entered
  const calculatedAmount = type === "fuel" && gallons && pricePerGallon && !amount
    ? (parseFloat(gallons) * parsedPrice).toFixed(2)
    : null;

  // Auto-calculate gallons when amount + price are entered
  const calculatedGallons = type === "fuel" && amount && pricePerGallon && !gallons && parsedPrice > 0
    ? (parseCurrency(amount) / 100 / parsedPrice).toFixed(3)
    : null;

  const resetForm = () => {
    setType("fuel");
    setAmount("");
    setGallons("");
    setPricePerGallon("");
    setRewardsPercent("");
    setUpsidePerGallon("");
    setDescription("");
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Determine final amount (manual, calculated from gallons, or entered directly)
    let finalAmount: number;
    if (type === "fuel" && calculatedAmount) {
      finalAmount = parseCurrency(calculatedAmount);
    } else {
      finalAmount = parseCurrency(amount);
    }

    if (finalAmount <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    // Determine final gallons (manual or calculated from amount)
    const finalGallons = type === "fuel"
      ? (parseFloat(gallons) || (calculatedGallons ? parseFloat(calculatedGallons) : undefined))
      : undefined;

    if (type === "other" && !description.trim()) {
      setError("Please enter a description");
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === "edit" && expense) {
        await updateExpense({
          id: expense._id,
          type,
          amount: finalAmount,
          gallons: finalGallons,
          pricePerGallon: type === "fuel" && parsedPrice > 0 ? Math.round(parsedPrice * 100) : undefined,
          rewardsPercent: type === "fuel" && rewardsPercent ? parseFloat(rewardsPercent) : undefined,
          upsidePerGallon: type === "fuel" && upsidePerGallon ? Math.round(parseFloat(upsidePerGallon) * 100) : undefined,
          description: description.trim() || undefined,
        });
      } else {
        await createExpense({
          blockId,
          type,
          amount: finalAmount,
          date: new Date().toISOString().split("T")[0],
          gallons: finalGallons,
          pricePerGallon: type === "fuel" && parsedPrice > 0 ? Math.round(parsedPrice * 100) : undefined,
          rewardsPercent: type === "fuel" && rewardsPercent ? parseFloat(rewardsPercent) : undefined,
          upsidePerGallon: type === "fuel" && upsidePerGallon ? Math.round(parseFloat(upsidePerGallon) * 100) : undefined,
          description: description.trim() || undefined,
        });
      }

      onSuccess?.();
      resetForm();
      setIsOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="bottom" className="h-auto max-h-[85vh] flex flex-col">
        <SheetHeader>
          <SheetTitle>{mode === "edit" ? "Edit Expense" : "Add Expense"}</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 mt-6 pb-4" id="expense-form">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">
              {error}
            </div>
          )}

          {/* Expense Type */}
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPENSE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    <div className="flex items-center gap-2">
                      <t.icon className="h-4 w-4" />
                      {t.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fuel-specific fields */}
          {type === "fuel" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="gallons">
                    Gallons {calculatedGallons ? "(calculated)" : ""}
                  </Label>
                  {calculatedGallons ? (
                    <div className="h-10 px-3 py-2 border rounded-md bg-muted font-medium">
                      {calculatedGallons}
                    </div>
                  ) : (
                    <Input
                      id="gallons"
                      type="number"
                      inputMode="decimal"
                      step="0.001"
                      min="0"
                      placeholder="12.5"
                      value={gallons}
                      onChange={(e) => setGallons(e.target.value)}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pricePerGallon">$/Gallon</Label>
                  <Input
                    id="pricePerGallon"
                    type="number"
                    inputMode="decimal"
                    step="0.001"
                    min="0"
                    placeholder="3.299"
                    value={pricePerGallon}
                    onChange={(e) => setPricePerGallon(e.target.value)}
                  />
                </div>
              </div>
              {/* Amount (auto-calculated for fuel, or manual entry) */}
              <div className="space-y-2">
                <Label htmlFor="amount">
                  Total {calculatedAmount ? "(calculated)" : ""}
                </Label>
                {calculatedAmount ? (
                  <div className="h-10 px-3 py-2 border rounded-md bg-muted text-lg font-semibold">
                    ${calculatedAmount}
                  </div>
                ) : (
                  <Input
                    id="amount"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="Or enter total to calculate gallons"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required={!calculatedAmount}
                  />
                )}
                {!gallons && !amount && (
                  <p className="text-xs text-muted-foreground">
                    Enter gallons to calculate total, or enter total to calculate gallons
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="rewardsPercent">Rewards %</Label>
                <div className="relative">
                  <Input
                    id="rewardsPercent"
                    type="number"
                    inputMode="decimal"
                    step="1"
                    min="0"
                    max="100"
                    placeholder="10"
                    value={rewardsPercent}
                    onChange={(e) => setRewardsPercent(e.target.value)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                    %
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="upsidePerGallon">Rewards $/gal</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                    $
                  </span>
                  <Input
                    id="upsidePerGallon"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="0.25"
                    value={upsidePerGallon}
                    onChange={(e) => setUpsidePerGallon(e.target.value)}
                    className="pl-7"
                  />
                </div>
              </div>
            </>
          )}

          {/* Amount for non-fuel expenses */}
          {type !== "fuel" && (
            <div className="space-y-2">
              <Label htmlFor="amount">Total</Label>
              <Input
                id="amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              {type === "other" ? "Description *" : "Notes (optional)"}
            </Label>
            <Textarea
              id="description"
              placeholder={type === "other" ? "What was this expense for?" : "Optional notes..."}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[60px] resize-none"
            />
          </div>

        </form>

        {/* Sticky bottom buttons - extra padding for bottom nav bar */}
        <div className="sticky bottom-0 bg-background border-t -mx-6 px-6 py-3 pb-[calc(env(safe-area-inset-bottom)+4rem)] mt-auto">
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" form="expense-form" className="flex-1" disabled={isSubmitting}>
              {isSubmitting
                ? mode === "edit" ? "Saving..." : "Adding..."
                : mode === "edit" ? "Save Changes" : "Add Expense"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
